import { useState, ChangeEvent } from 'react'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { getAttendanceSyncLogMetadata } from '../../services'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { AttendanceSyncResult } from '../../types/response'
import { Loader2, RefreshCcw } from 'lucide-react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Dialog } from 'vnetwork-ui'

interface MetadataDialogProps {
    open: boolean
    onClose: () => void
    syncLogId: number | null
    syncLogDate: string | null
}

const MetadataDialog = ({
    open,
    onClose,
    syncLogId,
    syncLogDate,
}: MetadataDialogProps) => {
    const { t } = useTranslation('workday')
    const [searchTerm, setSearchTerm] = useState('')

    const { data, isLoading, isError, refetch, isRefetching } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.ATTENDANCE_SYNC_LOG_METADATA, syncLogId],
            queryFn: () => getAttendanceSyncLogMetadata(syncLogId!),
            enabled: !!syncLogId && open,
        })

    const metadata = data?.result

    const filterEmployees = (list: AttendanceSyncResult[] | undefined) => {
        if (!list) return []
        if (!searchTerm) return list
        const lowerTerm = searchTerm.toLowerCase()
        return list.filter(
            (item) =>
                item.employee_code?.toLowerCase().includes(lowerTerm) ||
                item.employee_name?.toLowerCase().includes(lowerTerm)
        )
    }

    const EmployeeTable = ({
        employees,
    }: {
        employees: AttendanceSyncResult[]
    }) => {
        if (!employees || employees.length === 0) {
            return (
                <div className="p-4 text-center text-gray-500">
                    {t('attendance_sync_logs.no_data') || 'No data'}
                </div>
            )
        }
        return (
            <div className="max-h-[60vh] overflow-y-auto border rounded-md">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>
                                {t('attendance_sync_logs.employee_code') ||
                                    'Employee Code'}
                            </TableHead>
                            <TableHead>
                                {t('attendance_sync_logs.employee_name') ||
                                    'Employee Name'}
                            </TableHead>
                            <TableHead>
                                {t('attendance_sync_logs.time_in') || 'Time In'}
                            </TableHead>
                            <TableHead>
                                {t('attendance_sync_logs.time_out') ||
                                    'Time Out'}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {employees.map((emp, index) => (
                            <TableRow key={index}>
                                <TableCell>{emp.employee_code}</TableCell>
                                <TableCell>{emp.employee_name}</TableCell>
                                <TableCell>
                                    {emp.time_in ? emp.time_in : '-'}
                                </TableCell>
                                <TableCell>
                                    {emp.time_out ? emp.time_out : '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('attendance_sync_logs.sync_details') || 'Sync Details'}
            maxWidth="md"
            actions={
                <Button onClick={onClose}>
                    {t('attendance_sync_logs.close') || 'Close'}
                </Button>
            }
        >
            {isLoading ? (
                <div className="flex justify-center p-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                </div>
            ) : isError ? (
                <div className="text-red-500 p-4">
                    {t('attendance_sync_logs.error_loading_metadata') ||
                        'Error loading data'}
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    <div className="flex justify-between">
                        <div className="flex items-center gap-2">
                            <span className="font-semibold">
                                {t('attendance_sync_logs.sync_date') ||
                                    'Sync Date'}
                                :
                            </span>
                            <span>{syncLogDate}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {isRefetching ? (
                                <Loader2 className="size-5 animate-spin text-blue-800" />
                            ) : (
                                <RefreshCcw
                                    onClick={() => refetch()}
                                    className="size-5 cursor-pointer text-blue-800"
                                />
                            )}
                        </div>
                    </div>
                    <Tabs defaultValue="created" className="w-full">
                        <TabsList className="grid w-full grid-cols-3">
                            <TabsTrigger value="created">
                                {t('attendance_sync_logs.created') || 'Created'}{' '}
                                ({metadata?.created?.length || 0})
                            </TabsTrigger>
                            <TabsTrigger value="updated">
                                {t('attendance_sync_logs.updated') || 'Updated'}{' '}
                                ({metadata?.updated?.length || 0})
                            </TabsTrigger>
                            <TabsTrigger value="skipped">
                                {t('attendance_sync_logs.skipped') || 'Skipped'}{' '}
                                ({metadata?.skipped?.length || 0})
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent
                            value="created"
                            className="p-2 space-y-4 mt-4"
                        >
                            <Input
                                placeholder={
                                    t(
                                        'attendance_sync_logs.search_by_code_or_name'
                                    ) || 'Search by code or name...'
                                }
                                value={searchTerm}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setSearchTerm(e.target.value)
                                }
                                className="w-[50%]"
                            />
                            <EmployeeTable
                                employees={filterEmployees(metadata?.created)}
                            />
                        </TabsContent>
                        <TabsContent
                            value="updated"
                            className="p-2 space-y-4 mt-4"
                        >
                            <Input
                                placeholder={
                                    t(
                                        'attendance_sync_logs.search_by_code_or_name'
                                    ) || 'Search by code or name...'
                                }
                                value={searchTerm}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setSearchTerm(e.target.value)
                                }
                                className="w-[50%]"
                            />
                            <EmployeeTable
                                employees={filterEmployees(metadata?.updated)}
                            />
                        </TabsContent>
                        <TabsContent
                            value="skipped"
                            className="p-2 space-y-4 mt-4"
                        >
                            <Input
                                placeholder={
                                    t(
                                        'attendance_sync_logs.search_by_code_or_name'
                                    ) || 'Search by code or name...'
                                }
                                value={searchTerm}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setSearchTerm(e.target.value)
                                }
                                className="w-[50%]"
                            />
                            <EmployeeTable
                                employees={filterEmployees(metadata?.skipped)}
                            />
                        </TabsContent>
                    </Tabs>
                </div>
            )}
        </Dialog>
    )
}

export default MetadataDialog
